/* LINEのお友だち特典（スープ150g）をカートで選ぶ欄（2026-10-01 試作）。snippets/kazu-line-soup.liquid と組で使う。
   状態：manual（アプリ未接続＝今の手順）／loading・checking／login（未確認）／friend（選べる）／blocked（ブロックの記録あり）／
         not_friend（友だちではないが過去は不明）／expired（確認の期限切れ）／error（通信など。未登録とは言わない）
   友だちかどうかはアプリ（data-endpoint）が決める。テーマは表示と、カート属性への記録だけを行う。
   アプリとの受け渡しは deliverables/line-soup-v1/APP_DEV_SPEC.md の「5. テーマとの受け渡し」と同じ：
     POST {endpoint}/auth/start  { challenge, returnPath }        → { flow, url }   … url へ移動してLINEログイン
     （LINEのあと、ストアの returnPath#kls=<flow>&klp=<proof> に戻る。proof はLINEから戻ったブラウザだけが受け取る確認値）
     POST {endpoint}/auth/finish { flow, verifier, proof }        → { status: 'connected', session, expiresAt } | { status: 'pending' }
     （アプリ側の失敗で戻るときは #kls_error=<code>。line_login_required → expired、ほか → error 表示）
     POST {endpoint}/status      Authorization: Bearer <session>  → { state, checkedAt }
     POST {endpoint}/claim       Authorization: Bearer <session>  { want, cartToken } → { claimId, expiresAt }
   セッションはストア側の localStorage に置く（アプリのドメインのCookieには頼らない）。
   プレビュー専用：ローカルのプレビュー（<meta name="kls-preview">があるページ）だけ、URLの ?kls_mock=<状態> でアプリなしの表示を出せる。本番テーマでは無効。 */
(() => {
  if (window.kazuLineSoupLoaded) return; // main-cart と引き出しカートの両方で読まれても1回だけ
  window.kazuLineSoupLoaded = true;
  const ATTR = 'LINEスープ特典';
  const ATTR_ID = 'LINEスープ照合ID';
  const WANT = '希望する';
  const SESSION_KEY = 'kazu-line-soup:session';   // { session, expiresAt }
  const FLOW_KEY = 'kazu-line-soup:flow';         // { flow, verifier, savedAt }（localStorage。LINEから新しいタブで戻っても読めるように。10分で無効）
  const FLOW_TTL_MS = 10 * 60 * 1000;
  const RETURN_KEY = 'kazu-line-soup:return';     // { kls, klp, kls_error }（sessionStorage。snippet の先頭でURLから移して消す）
  const CLAIM_KEY = 'kazu-line-soup:claim';       // { claimId, issuedAt, cartToken }
  const CLAIM_REFRESH_MS = 60 * 60 * 1000;        // 1時間より古い照合用IDは、カートを開いたときに取り直す
  const mock = document.querySelector('meta[name="kls-preview"]') ? new URLSearchParams(location.search).get('kls_mock') : null;
  let returnError = ''; // LINEから #kls_error=<code> で戻ったときのコード
  let statusPromise = null;
  let claimChecked = false; // 照合用IDの取り直しはページごとに1回

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const store = {
    get(area, key) { try { return JSON.parse(area.getItem(key) || 'null'); } catch (e) { return null; } },
    set(area, key, value) { try { value == null ? area.removeItem(key) : area.setItem(key, JSON.stringify(value)); } catch (e) { /* 保存できない環境では毎回確認 */ } },
  };
  const b64url = (bytes) => btoa(String.fromCharCode(...new Uint8Array(bytes))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  const cartToken = async () => (await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json())).token;

  const post = async (root, path, body, auth) => {
    const headers = { 'Content-Type': 'application/json' };
    if (auth) headers.Authorization = 'Bearer ' + auth;
    const res = await fetch(root.dataset.endpoint + path, { method: 'POST', headers, body: JSON.stringify(body || {}) });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data };
  };

  const session = () => {
    const s = store.get(localStorage, SESSION_KEY);
    if (!s || !s.session || (s.expiresAt && s.expiresAt < Date.now())) return null;
    return s.session;
  };

  const api = {
    configured: (root) => Boolean(mock || root.dataset.endpoint),

    async login(root) {
      if (mock) { const url = new URL(location.href); url.searchParams.set('kls_mock', 'friend'); location.href = url.href; return; }
      const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)));
      const challenge = b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
      // 2026-10-04：LINEのあとは、いつもカートのページへ戻す（引き出しカートを開いていたページ＝トップなどに戻ると、カートを開き直す手間になる：ユーザー）
      const res = await post(root, '/auth/start', { challenge, returnPath: '/cart' });
      if (!res.ok || !res.data.url) throw new Error('auth start ' + res.status);
      store.set(localStorage, FLOW_KEY, { flow: res.data.flow, verifier, savedAt: Date.now() });
      location.href = res.data.url;
    },

    // LINEから戻ったとき（#kls=<flow>&klp=<proof>）にセッションを受け取る。proof はURLから消したあと、この中だけで使う
    async finish(root) {
      let ret = store.get(sessionStorage, RETURN_KEY);
      store.set(sessionStorage, RETURN_KEY, null);
      if (!ret && /(^#|&)kls(_error)?=/.test(location.hash)) {
        ret = Object.fromEntries(new URLSearchParams(location.hash.slice(1)));
        history.replaceState(null, '', location.pathname + location.search);
      }
      if (!ret) return;
      if (ret.kls_error) { store.set(localStorage, FLOW_KEY, null); returnError = String(ret.kls_error); return; }
      const flow = ret.kls;
      const proof = ret.klp;
      const saved = store.get(localStorage, FLOW_KEY);
      store.set(localStorage, FLOW_KEY, null);
      if (!saved || saved.flow !== flow || !(Date.now() - (saved.savedAt || 0) < FLOW_TTL_MS) || !/^[\w-]{43}$/.test(flow || '') || !/^[\w-]{43}$/.test(proof || '')) return;
      for (let i = 0; i < 4; i += 1) {
        const res = await post(root, '/auth/finish', { flow, verifier: saved.verifier, proof });
        if (res.ok && res.data.status === 'connected') { store.set(localStorage, SESSION_KEY, { session: res.data.session, expiresAt: res.data.expiresAt }); return; }
        if (!(res.ok && res.data.status === 'pending')) return;
        await wait(700);
      }
    },

    async status(root) {
      if (mock) { await wait(250); return { state: mock }; }
      // 確認の流れの期限切れ（line_login_required）は「期限切れ」、それ以外（not_ready・please_retry_later・invalid_request）は「確認できませんでした」
      if (returnError) { const code = returnError; returnError = ''; return { state: code === 'line_login_required' ? 'expired' : 'error' }; }
      const token = session();
      if (!token) return { state: store.get(localStorage, SESSION_KEY) ? 'expired' : 'login' };
      const res = await post(root, '/status', {}, token);
      if (res.status === 401) { store.set(localStorage, SESSION_KEY, null); return { state: 'expired' }; }
      if (!res.ok) throw new Error('status ' + res.status);
      return res.data;
    },

    async claim(root, want) {
      if (mock) { await wait(200); return { claimId: want ? 'mock-' + Math.random().toString(36).slice(2, 12) : '' }; }
      const token = session();
      if (!token) throw new Error('no session');
      const cart = await cartToken();
      const res = await post(root, '/claim', { want, cartToken: cart }, token);
      if (res.status === 401) store.set(localStorage, SESSION_KEY, null);
      if (!res.ok) throw Object.assign(new Error('claim ' + res.status), { status: res.status });
      store.set(localStorage, CLAIM_KEY, want ? { claimId: res.data.claimId, issuedAt: Date.now(), cartToken: cart } : null);
      return res.data;
    },
  };

  // カート属性：希望する／空（＝属性を消す）。プレビューでは本番のカートに書き込まない。
  const saveAttributes = async (want, claimId) => {
    const attributes = { [ATTR]: want ? WANT : '', [ATTR_ID]: want ? claimId : '' };
    if (mock) { sessionStorage.setItem('kls-preview', JSON.stringify(attributes)); return; }
    const res = await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes }) });
    if (!res.ok) throw new Error('cart ' + res.status);
  };

  const allRoots = () => Array.from(document.querySelectorAll('[data-kls]'));

  const setState = (root, state) => {
    root.dataset.state = state;
    root.querySelectorAll('[data-kls-panel]').forEach((panel) => {
      panel.hidden = !panel.dataset.klsPanel.split(' ').includes(state);
    });
  };

  // 同じページのカート欄と引き出しカート欄をそろえる
  const syncWant = (want) => {
    allRoots().forEach((root) => {
      root.dataset.want = String(want);
      const box = root.querySelector('[data-kls-want]');
      if (box) box.checked = want;
    });
  };

  // 「希望する」のまま時間がたったら、照合用IDを取り直して注文時に期限切れにしない
  const refreshClaim = async (root) => {
    if (mock || root.dataset.want !== 'true') return;
    const saved = store.get(localStorage, CLAIM_KEY);
    const token = await cartToken().catch(() => null);
    if (saved && saved.cartToken === token && Date.now() - saved.issuedAt < CLAIM_REFRESH_MS) return;
    try { const { claimId } = await api.claim(root, true); if (claimId) await saveAttributes(true, claimId); }
    catch (e) { await saveAttributes(false, '').catch(() => {}); syncWant(false); }
  };

  const check = async (root, force) => {
    if (!api.configured(root)) { setState(root, 'manual'); return; }
    setState(root, force ? 'checking' : 'loading');
    if (force || !statusPromise) statusPromise = api.finish(root).catch(() => {}).then(() => api.status(root));
    let state = 'error';
    try { state = (await statusPromise).state || 'error'; } catch (e) { statusPromise = null; }
    allRoots().filter((r) => r.dataset.eligible === 'true').forEach((r) => setState(r, state));
    if (state === 'friend') { if (!claimChecked) { claimChecked = true; refreshClaim(root); } return; }
    // 選べない状態なのに「希望する」が残っていたら消す（注文で要確認にならないように）
    if (root.dataset.want === 'true') {
      try { await saveAttributes(false, ''); syncWant(false); } catch (e) { /* 次の表示で再度消す */ }
    }
  };

  const init = (root) => {
    if (root.dataset.klsReady) return;
    root.dataset.klsReady = 'true';

    if (root.dataset.eligible !== 'true') {
      // 冷凍そばがなくなったカートに希望が残っていたら消す
      if (root.dataset.want === 'true') saveAttributes(false, '').then(() => syncWant(false)).catch(() => {});
      return;
    }

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-kls-login]')) api.login(root).catch(() => setState(root, 'error'));
      if (event.target.closest('[data-kls-recheck]')) check(root, true);
    });

    root.addEventListener('change', async (event) => {
      const box = event.target.closest('[data-kls-want]');
      if (!box) return;
      const want = box.checked;
      const error = root.querySelector('[data-kls-save-error]');
      box.disabled = true;
      if (error) error.hidden = true;
      try {
        // 2026-10-04：外すときはアプリに問い合わせず、カートの記録（希望・照合ID）を消すだけ。照合IDがなければ注文で同梱されない。
        //   （以前はアプリの返事を待ってから保存していたため、返事がないとチェックが元に戻り「外せない」状態になった：ユーザー）
        if (!want) {
          store.set(localStorage, CLAIM_KEY, null);
          await saveAttributes(false, '');
          syncWant(false);
          return;
        }
        const { claimId } = await api.claim(root, want);
        if (!claimId) throw new Error('no claim');
        await saveAttributes(want, claimId);
        syncWant(want);
      } catch (e) {
        box.checked = !want;
        // 期限切れ（401）・友だちでなくなった（409）は、表示を確認し直す。それ以外は保存できなかった旨だけ出す
        if (e.status === 401 || e.status === 409) check(root, true);
        else if (error) error.hidden = false;
      } finally {
        box.disabled = false;
      }
    });

    check(root, false);
  };

  const scan = () => allRoots().forEach(init);
  scan();
  // 引き出しカートは中身が入れ替わるので、新しく入った欄も初期化する
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
})();
